import { useState } from 'react'

import { performRequestMassAction } from '../../../../../api'
import {
  useIsMountedRef,
  useMassActions,
  useNotification,
  useRefetchCounters,
  useVariableRef,
} from '../../../../../hooks'
import { REQUEST_ACTION_UPDATE_STATUS_ITEM } from '../../../Request.constants.actions'
import {
  getAsyncNotificationText,
  getNotificationTitle,
} from '../../../Request.utils.notifications'
import { getRequestBoardColumnCount } from '../../RequestBoard.api'
import { REQUEST_BOARD_COLUMN_FIRST_INDEX } from '../../RequestBoard.constants'
import {
  getRequestBoardColumn,
  getRequestBoardItemFilters,
  removeRequestBoardId,
} from '../../RequestBoard.utils'

import { REQUEST_BOARD_CHANGE_STATUS_TOTAL_COUNT } from './useRequestBoardChangeStatus.constants'
import {
  getRequestBoardChangeStatusParams,
  getRequestBoardDragData,
  getRequestBoardDropStatus,
  getRequestBoardMove,
  getRequestBoardMoveColumns,
  getRequestBoardMovedItem,
  isRequestBoardCardMoved,
  isRequestBoardColumnRecountDue,
  isRequestBoardInsertRolledBack,
  isRequestBoardItemListed,
  isRequestBoardListingCheckNeeded,
  isRequestBoardMoveOutdated,
  isRequestBoardMoveTargetListed,
  isRequestBoardSourceReloadNeeded,
  isRequestBoardStatusChanged,
  isRequestBoardTableReloadNeeded,
} from './useRequestBoardChangeStatus.utils'

export function useRequestBoardChangeStatus({
  columns,
  filterValues,
  sub,
  onReloadList,
  onReloadTable,
  onSetPendingIds,
}) {
  const refetchCounters = useRefetchCounters()

  const { handleAddErrorNotification } = useNotification()

  const { handleAddFinishMassActionNotification } = useMassActions({
    getAsyncNotificationText,
    getNotificationTitle,
  })

  const [draggedStatus, setDraggedStatus] = useState(null)
  const [dropStatus, setDropStatus] = useState(null)

  const columnsRef = useVariableRef(columns)
  const filterValuesRef = useVariableRef(filterValues)

  const isMountedRef = useIsMountedRef()

  async function loadIsItemListed(move) {
    return isRequestBoardListingCheckNeeded(filterValuesRef.current)
      ? getRequestBoardColumnCount(
          getRequestBoardItemFilters({
            filterValues: filterValuesRef.current,
            id: move.item.id,
          })
        )
          .then(({ data }) => isRequestBoardItemListed(data))
          .catch(() => true)
      : isRequestBoardMoveTargetListed({ columns: columnsRef.current, move })
  }

  function handleSettleMove({ isChanged, isListed = true, move }) {
    const { source, target } = getRequestBoardMoveColumns({
      columns: columnsRef.current,
      move,
    })

    source.handleSettleDepartedItem({ id: move.item.id, isChanged })

    if (move.isInserted) {
      target.handleSettleInsertedItem(move.item.id)
    }

    if (isRequestBoardMoveOutdated({ move, source, target })) {
      source.handleReload()
      target.handleReload()
    } else {
      if (isRequestBoardInsertRolledBack({ isChanged, isListed, move })) {
        target.handleRemoveItem(move.item.id)
      }

      if (isRequestBoardSourceReloadNeeded({ isChanged, move })) {
        source.handleReload()
      } else if (
        isRequestBoardColumnRecountDue({ column: source, id: move.item.id })
      ) {
        source.handleReloadCount()
      }

      if (
        isRequestBoardColumnRecountDue({ column: target, id: move.item.id })
      ) {
        target.handleReloadCount()
      }
    }
  }

  // A board replaced while the request ran only reloads the list: its columns
  // are gone.
  function handleSettleSavedMove({ isListed, move }) {
    if (isMountedRef.current) {
      handleSettleMove({ isChanged: true, isListed, move })

      if (
        isRequestBoardTableReloadNeeded({
          columns: columnsRef.current,
          filterValues: filterValuesRef.current,
          isListed,
          move,
        })
      ) {
        onReloadTable()
      }
    } else {
      onReloadList()
    }
  }

  async function handleChangeStatusSuccess({ data, move }) {
    handleAddFinishMassActionNotification(data, {
      action: REQUEST_ACTION_UPDATE_STATUS_ITEM,
      totalCount: REQUEST_BOARD_CHANGE_STATUS_TOTAL_COUNT,
    })

    refetchCounters()

    return isMountedRef.current
      ? loadIsItemListed(move).then(isListed =>
          handleSettleSavedMove({ isListed, move })
        )
      : onReloadList()
  }

  function handleChangeStatusFailure(move) {
    if (isMountedRef.current) {
      handleSettleMove({ isChanged: false, move })
    }

    handleAddErrorNotification('Notification.RequestMassActionErrorKey')
  }

  function handleMoveCard({ item, source, target }) {
    const move = getRequestBoardMove({ filterValues, item, source, target })

    source.handleDepartItem(item.id)

    if (move.isInserted) {
      target.handleInsertItem({
        index: REQUEST_BOARD_COLUMN_FIRST_INDEX,
        item: getRequestBoardMovedItem({ item, status: target.status }),
      })
    }

    onSetPendingIds(prevIds => [...prevIds, item.id])

    performRequestMassAction(
      getRequestBoardChangeStatusParams({
        filterValues,
        id: item.id,
        status: target.status,
        sub,
      })
    )
      .then(({ data }) =>
        isRequestBoardStatusChanged(data)
          ? handleChangeStatusSuccess({ data, move })
          : handleChangeStatusFailure(move)
      )
      .catch(() => {
        handleChangeStatusFailure(move)
      })
      .finally(() => {
        onSetPendingIds(prevIds =>
          removeRequestBoardId({ id: item.id, ids: prevIds })
        )
      })
  }

  function handleDragStart(event) {
    setDraggedStatus(getRequestBoardDragData(event).status)
    setDropStatus(null)
  }

  function handleDragOver(event) {
    setDropStatus(getRequestBoardDropStatus(event))
  }

  function handleDragCancel() {
    setDraggedStatus(null)
    setDropStatus(null)
  }

  function handleDragEnd(event) {
    const { item, status: sourceStatus } = getRequestBoardDragData(event)

    const targetStatus = getRequestBoardDropStatus(event)

    setDraggedStatus(null)
    setDropStatus(null)

    if (isRequestBoardCardMoved({ item, sourceStatus, targetStatus })) {
      handleMoveCard({
        item,
        source: getRequestBoardColumn({ columns, status: sourceStatus }),
        target: getRequestBoardColumn({ columns, status: targetStatus }),
      })
    }
  }

  return {
    draggedStatus,
    dropStatus,
    handleDragCancel,
    handleDragEnd,
    handleDragOver,
    handleDragStart,
  }
}
